"use client";

import { useState, useRef } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import {
    ArrowRight,
    Rocket,
    Brain,
    LayoutDashboard,
  } from "lucide-react";
import { link } from "fs";

export default function ProofOfWork() {
const [hoveredStudy, setHoveredStudy] = useState<string | null>(null);

const scrollRef = useRef<HTMLDivElement>(null);

const [isDragging, setIsDragging] = useState(false);
const [startX, setStartX] = useState(0);
const [scrollLeft, setScrollLeft] = useState(0);
const caseStudies = [
    {
      id: "alliesia",
      icon: Brain,
      title: "Alliesia — AI Voice Assistant & Productivity Platform",
      link: "https://alliesia.com",
      summary:
        "An AI-powered productivity platform that helps teams manage meetings, tasks, follow-ups, and workflows through intelligent voice and chat interactions.",
      focus: [
        "AI Assistant",
        "Productivity",
        "Automation",
      ],
    },
  
    {
      id: "whoogy",
      icon: LayoutDashboard,
      title: "Whoogy — AI-Powered QA Testing Platform",
      link: "https://whoogy.com",
      summary:
        "A modern quality assurance platform designed to help teams automate UI testing, identify issues faster, and improve software reliability.",
      focus: [
        "QA Testing",
        "Automation",
        "Figma to web testing",
      ],
    },
  
    {
      id: "mvp-builds",
      icon: Rocket,
      title: "Custom MVPs & SaaS Products",
      link: "/products",
      summary:
        "We help founders transform ideas into launch-ready MVPs and scalable SaaS products with modern architecture, great user experience, and long-term growth in mind.",
      focus: [
        "MVP Development",
        "SaaS Products",
        "Scalability",
      ],
    },
  ];
  const cardStyles = {
    sectionBg: {
      background: `
        radial-gradient(circle at top left, rgba(237,124,63,0.08) 0%, transparent 35%),
        radial-gradient(circle at top right, rgba(70,100,255,0.08) 0%, transparent 35%),
        #07111F
      `,
    },
  
    card: {
      background: "#111C2D",
      border: "1px solid rgba(255,255,255,0.06)",
      boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
    },
  
    visualArea: {
      background:
        "linear-gradient(135deg, rgba(237,124,63,0.12) 0%, rgba(90,125,255,0.08) 100%)",
    },
  
    iconBox: {
      background: "rgba(237,124,63,0.12)",
      border: "1px solid rgba(237,124,63,0.35)",
    },
  
    iconColor: "#ED7C3F",
  
    accentLine:
      "linear-gradient(90deg, transparent, rgba(237,124,63,0.7), transparent)",
  
    bodyText: "rgba(255,255,255,0.65)",
  
    tagBg: "rgba(255,255,255,0.06)",
  
    tagActiveBg: "rgba(237,124,63,0.15)",
  
    tagActiveText: "#ED7C3F",
  };
    

const handleMouseDown = (
e: React.MouseEvent<HTMLDivElement>
) => {
if (!scrollRef.current) return;


setIsDragging(true);
setStartX(e.pageX - scrollRef.current.offsetLeft);
setScrollLeft(scrollRef.current.scrollLeft);


};

const handleMouseUp = () => {
setIsDragging(false);
};

const handleMouseLeave = () => {
setIsDragging(false);
};

const handleMouseMove = (
e: React.MouseEvent<HTMLDivElement>
) => {
if (!isDragging || !scrollRef.current) return;

e.preventDefault();

const x = e.pageX - scrollRef.current.offsetLeft;
const walk = (x - startX) * 1.5;

scrollRef.current.scrollLeft =
  scrollLeft - walk;

};

return (  <section
    className="relative py-20 overflow-hidden"
    style={cardStyles.sectionBg}
  >
    <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
  

  {/* Header */}
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="mb-12"
  >
    <h2
      className="text-[28px] sm:text-[34px] md:text-[40px] text-white mb-4"
      style={{
        fontWeight: 400,
        lineHeight: 1.15,
        letterSpacing: "-0.02em",
      }}
    >
      Building Products That Scale
    </h2>
  
    <p
      className="text-[16px] md:text-[17px] max-w-[650px]"
      style={{
        color: "rgba(255,255,255,0.65)",
        fontWeight: 400,
        lineHeight: 1.6,
      }}
    >
      From startup MVPs to AI-powered platforms,
      we help founders and businesses turn ideas
      into scalable software products.
    </p>
  </motion.div>
  
  
    <div
      ref={scrollRef}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    className={`flex gap-6 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 scrollbar-hide ${
      isDragging
        ? "cursor-grabbing"
        : "cursor-grab lg:cursor-default"
    }`}
  >
    {caseStudies?.map((study, index) => {
      const Icon = study.icon;
      const isHovered = hoveredStudy === study.id;
  
      return (
        <motion.div
          key={study.id}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.4,
            delay: index * 0.1,
          }}
          onMouseEnter={() =>
            setHoveredStudy(study.id)
          }
          onMouseLeave={() =>
            setHoveredStudy(null)
          }
          className="flex-shrink-0 w-[340px] lg:flex-1 lg:w-auto"
        >
          <Link href={study.link} target="_blank">
          <div
            className="h-full overflow-hidden rounded-2xl flex flex-col transition-all duration-300"
            style={{
              ...cardStyles.card,
              borderColor: isHovered
                ? "rgba(237,124,63,0.35)"
                : "rgba(255,255,255,0.06)",
              transform: isHovered
                ? "translateY(-4px)"
                : "translateY(0)",
            }}
          >
            <div
              className="h-40 relative overflow-hidden"
              style={cardStyles.visualArea}
            >
              <div className="absolute inset-0 opacity-25">
                <svg width="100%" height="100%">
                  <defs>
                    <pattern
                      id={`grid-${study.id}`}
                      width="32"
                      height="32"
                      patternUnits="userSpaceOnUse"
                    >
                      <path
                        d="M 32 0 L 0 0 0 32"
                        fill="none"
                        stroke="rgba(255,255,255,0.12)"
                        strokeWidth="0.5"
                      />
                    </pattern>
                  </defs>
  
                  <rect
                    width="100%"
                    height="100%"
                    fill={`url(#grid-${study.id})`}
                  />
                </svg>
              </div>
  
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-300"
                  style={{
                    ...cardStyles.iconBox,
                    transform: isHovered
                      ? "scale(1.08)"
                      : "scale(1)",
                  }}
                >
                  <Icon
                    className="w-7 h-7"
                    color={cardStyles.iconColor}
                  />
                </div>
              </div>
  
              <div className="absolute bottom-4 left-6 right-6">
                <div
                  className="h-[1px]"
                  style={{
                    background:
                      cardStyles.accentLine,
                  }}
                />
              </div>
            </div>
  
            <div className="p-6 flex flex-col flex-grow">
              <h3
                className="text-[17px] text-white mb-4"
                style={{
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                {study.title}
              </h3>
  
              <p
                className="text-[14px] mb-5 flex-grow"
                style={{
                  color:
                    cardStyles.bodyText,
                  fontWeight: 400,
                  lineHeight: 1.7,
                }}
              >
                {study.summary}
              </p>
  
              <div className="grid grid-cols-2 gap-2 pt-4 border-t border-white/10">
                {study.focus.map(
                  (tag, idx) => (
                    <span
                      key={idx}
                      className={`text-[11px] px-2.5 py-1 rounded-full text-center ${
                        idx === 2
                          ? "col-span-2 justify-self-center"
                          : ""
                      }`}
                      style={{
                        fontWeight: 500,
                        background:
                          isHovered
                            ? cardStyles.tagActiveBg
                            : cardStyles.tagBg,
                        color: isHovered
                          ? cardStyles.tagActiveText
                          : "rgba(255,255,255,0.7)",
                      }}
                    >
                      {tag}
                    </span>
                  )
                )}
              </div>
            </div>
          </div></Link>
        </motion.div>
      );
    })}
  </div>
  
  <motion.div
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true }}
    transition={{
      duration: 0.4,
      delay: 0.3,
    }}
    className="mt-10"
  >
    <Link
      href="/case-studies"
      className="group flex items-center text-[14px] hover:translate-x-1 transition-all duration-200"
      style={{
        color: "#ED7C3F",
        fontWeight: 500,
      }}
    >
      View All Case Studies
  
      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
    </Link>
  </motion.div>

  
    </div>
  </section>

);
}
